import Cookies from 'js-cookie'
import { getUserBasicData } from './mixpanel-helper'

type MixpanelConfig = {
  debug?: boolean
  [key: string]: unknown
}

type Mixpanel = {
  init: (token: string, config: MixpanelConfig) => void
  track: (event_name: string, properties: Record<string, unknown>) => void
  track_pageview: (properties: Record<string, unknown>) => void
  identify: (id: string) => void
}

export type TMixPanelEventPayload = {
  eventName: string
  payload: Record<string, unknown>
}

const MixpanelManager = {
  initializationPromise: null as Promise<Mixpanel | null> | null,
  currentToken: null as string | null,
  isUserIdentified: false as boolean,

  async initialize() {
    try {
      await this.initializationPromise
    } catch {
      this.initializationPromise = null
    }

    if (!this.initializationPromise) {
      this.initializationPromise = this.setupMixpanel()
    }

    const mixpanelInstance = await this.initializationPromise

    const token = Cookies.get('token')
    if (token && (!this.isUserIdentified || this.currentToken !== token)) {
      const userId = await this.getUserId()
      this.currentToken = token
      this.isUserIdentified = false
      if (userId) {
        mixpanelInstance?.identify(userId)
        this.isUserIdentified = true
      }
    }
    return mixpanelInstance
  },

  async setupMixpanel(): Promise<Mixpanel | null> {
    try {
      const module = await import('mixpanel-browser')
      const mixpanelInstance = module.default

      if (process.env.NEXT_PUBLIC_MIXPANEL_PROJECT_TOKEN) {
        mixpanelInstance.init(process.env.NEXT_PUBLIC_MIXPANEL_PROJECT_TOKEN, {
          debug: process.env.NODE_ENV === 'development',
        })
      }

      return mixpanelInstance
    } catch (error: unknown) {
      console.error('Failed to initialize Mixpanel:', error)
      return null
    }
  },

  async getUserId(): Promise<string | null> {
    try {
      const token = Cookies.get('token')
      if (!token) return null

      const userData = await getUserBasicData({
        headers: { Authorization: `Bearer ${token}` },
        queryParams: {},
        fromMixpanel: true,
      })

      if (userData.id) {
        return userData.id.toString()
      }
    } catch (error: unknown) {
      console.error('Failed to identify user:', error)
      return null
    }
    return null
  },
}

export function mixpanelTrack({ eventName, payload }: TMixPanelEventPayload) {
  // return null
  if (!eventName && process.env.NODE_ENV === 'development') {
    console.info(`eventName not found for mixpanel track`)
    return
  }

  if (process.env.NODE_ENV === 'development') {
    console.info('mixpanel track event: ', { eventName, payload })
    return
  }

  void MixpanelManager.initialize().then(mixpanel => {
    if (mixpanel && process.env.NEXT_PUBLIC_MIXPANEL_PROJECT_TOKEN) {
      console.info('tracking')
      mixpanel.track(eventName, payload)
    }
  })
}

export function mixpanelPageView(pageOrScreenName: string, payload) {
  console.info(pageOrScreenName, payload)
  if (!pageOrScreenName) {
    console.info(`page name not found for mixpanel page view`)
    return
  }

  if (process.env.NODE_ENV === 'development') {
    console.info(
      `mixpanel page view event with "${pageOrScreenName}" page name: `,
      payload,
    )
  }

  const { pageOrScreenName: nothingToDoWithThis, ...rest } = payload

  void MixpanelManager.initialize().then(mixpanel => {
    if (mixpanel) {
      mixpanel.track_pageview({ page: pageOrScreenName, ...rest })
    }
  })
}
